import type { JSX } from 'react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppstoreOutlined,
  BellOutlined,
  BgColorsOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LeftOutlined,
  LogoutOutlined,
  MenuOutlined,
  RightOutlined,
  SearchOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Badge, Dropdown, Empty, Tooltip, type MenuProps } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { authApi, type WikiMeDto } from '../lib/authApi';
import { notificationsApi, openingApi, type WikiInboxNotification } from '../lib/wikiApi';
import { hubApi } from '../lib/api/hub';
import { isNavActive, visibleNav, visibleUserMenu, type NavBadgeKey, type NavItem } from '../lib/nav';
import { useThemeMode, THEME_LABEL, THEME_ORDER } from '../lib/theme';
import { CmdKSearch, openCmdK } from './CmdKSearch';
import { CreateMenu } from './CreateMenu';
import { HelpFab } from './HelpFab';
import { HubLogo } from './HubLogo';
import { MobileTabBar } from './MobileTabBar';
import { PreviewBanner } from './PreviewBanner';
import { stopViewAs, ViewAsBanner, ViewAsModal } from './ViewAs';

/**
 * Оболочка FISHKA HUB 2.0 (вариант 1a «classic», план wk6 §1.1, §2, §3).
 *
 *   ┌──────────┬──────────────────────────────────────────────┐
 *   │ fishka   │ [Поиск…  ⌘K]        [Создать ▾] 🔔 📍 [АО ▾] │
 *   │ HUB      ├──────────────────────────────────────────────┤
 *   │ ДЛЯ МЕНЯ │ main (children)                              │
 *   │ ЗНАНИЯ   │                                              │
 *   │ ОБУЧЕНИЕ │                                              │
 *   │ УПРАВЛ.  │                                          (?) │
 *   └──────────┴──────────────────────────────────────────────┘
 *
 * Меню — из lib/nav.ts (группы, видимость по роли, ключи бейджей); бейджи —
 * один запрос GET /wiki/hub/counts раз в минуту. Свёрнутый рейл с плашками
 * по ховеру — существующая фича «как в CRM», оставлена. Сворачивает меню
 * круглая кнопка «‹ / ›» на правой границе сайдбара на уровне логотипа —
 * как в CRM (frontend/src/layouts/AppSidebar.tsx, владелец 07.10.2026):
 * подсказка «Свернуть меню» / «Развернуть меню», ширина едет плавно,
 * состояние — в localStorage. <900 px сайдбар — выезжающая панель (кнопки
 * нет); <768 px — нижняя панель вкладок (MobileTabBar), «Ещё» открывает ту
 * же панель. Стили — `.fk-hub-*` в styles/global.css.
 *
 * «Смотреть от лица партнёра» (владелец 07.10.2026): пункт в меню профиля,
 * пока режим включён — жёлтая плашка под шапкой (components/ViewAs.tsx).
 * Плашка предпросмотра курса (components/PreviewBanner.tsx) — там же. Шапка
 * и обе плашки — один липкий блок `.fk-hub-top`, его высота — в
 * --fk-hub-header-h.
 *
 * «Нужна помощь?» (владелец 07.10.2026) — не блок внизу меню, а плавающая
 * круглая кнопка в правом нижнем углу (components/HelpFab.tsx). С 19.10.2026 это
 * общий с CRM и SMS-центром оранжевый «+» (стопка плашек): вопрос в УК, ошибка,
 * идея, мои обращения, вопрос по базе знаний.
 */

const COLLAPSE_KEY = 'fk-sidebar-collapsed';
const CRM_URL = 'https://crm.brasol.ru';

const useMe = (): WikiMeDto | undefined =>
  useQuery({ queryKey: ['wiki-auth', 'me'], queryFn: () => authApi.me(), retry: false, staleTime: 60_000 }).data;

export function Layout({ children }: { children: ReactNode }): JSX.Element {
  // Мобильная выезжающая панель (на десктопе ≥900 px флаг игнорирует CSS).
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Свёрнутый сайдбар (как в CRM): рейл с иконками; состояние — в localStorage.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const location = useLocation();
  const me = useMe();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = (): void => {
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        /* приватный режим — только на сессию */
      }
      return next;
    });
  };

  // Все бейджи меню — один лёгкий запрос раз в минуту (HUB 2.0).
  const countsQ = useQuery({
    queryKey: ['wiki', 'hub', 'counts'],
    queryFn: () => hubApi.counts(),
    enabled: !!me,
    refetchInterval: 60_000,
    retry: false,
  });
  const badges: Partial<Record<NavBadgeKey, number | null>> = countsQ.data ?? {};

  // Высота липкого верха (шапка + плашки) → --fk-hub-header-h: от неё
  // отсчитывают липкие колонки страниц, Диск и кнопка помощи. Плашка
  // включилась или перенеслась на две строки — колонки сдвигаются сами.
  const topRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const apply = (): void => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--fk-hub-header-h', `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--fk-hub-header-h');
    };
  }, []);

  return (
    <div className={`fk-shell fk-hub-shell${collapsed ? ' fk-shell-collapsed' : ''}`}>
      <Sidebar me={me} badges={badges} drawerOpen={drawerOpen} onClose={() => setDrawerOpen(false)} collapsed={collapsed} />
      {/* Круглая кнопка «‹ / ›» на правой границе сайдбара, как в CRM. Вне
          <aside>: у него overflow hidden, а кнопка наполовину выходит за край. */}
      <Tooltip title={collapsed ? 'Развернуть меню' : 'Свернуть меню'} placement="right">
        <button
          type="button"
          className="fk-hub-sider-toggle"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
          aria-expanded={!collapsed}
        >
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </button>
      </Tooltip>
      {drawerOpen && <div className="fk-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}
      <CmdKSearch />
      <div className="fk-hub-col">
        {/* Липкий верх: шапка + плашки режимов («от лица партнёра», предпросмотр
            курса) одним блоком — плашка не наезжает на шапку при прокрутке. */}
        <div className="fk-hub-top" ref={topRef}>
          <Header me={me} onMenu={() => setDrawerOpen(true)} />
          <ViewAsBanner me={me} />
          <PreviewBanner />
        </div>
        {/* overflowX: clip (не hidden) — не создаёт scroll-контейнер, sticky внутри страниц работает. */}
        <main className="fk-main">{children}</main>
      </div>
      <MobileTabBar badges={badges} moreOpen={drawerOpen} onMore={() => setDrawerOpen((v) => !v)} />
      {/* Внутри .fk-hub-shell: у левого края кнопка встаёт правее меню (--sidebar-width). */}
      {me && <HelpFab />}
    </div>
  );
}

// ─── Сайдбар ───────────────────────────────────────────────────────

function SideNavLink({ item, count, collapsed }: { item: NavItem; count: number | null | undefined; collapsed: boolean }): JSX.Element {
  const { pathname } = useLocation();
  const active = isNavActive(item, pathname);
  const Icon = item.icon;
  // В свёрнутом рейле подпись скрыта — имя ссылки для экранного диктора.
  const a11yName = collapsed ? (count ? `${item.label}: ${count}` : item.label) : undefined;
  return (
    <Link
      to={item.to}
      className="fk-navlink fk-hub-navlink"
      data-label={item.label}
      data-to={item.to}
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'page' : undefined}
      aria-label={a11yName}
    >
      {active && <span className="fk-hub-navrail" />}
      <span className="fk-hub-navicon">
        <Icon />
        {count ? <span className="fk-hub-navdot" /> : null}
      </span>
      <span className="fk-sidebar-label fk-hub-navtext">{item.label}</span>
      {count ? <span className="fk-sidebar-label fk-hub-badge">{count > 99 ? '99+' : count}</span> : null}
    </Link>
  );
}

function Sidebar({
  me,
  badges,
  drawerOpen,
  onClose,
  collapsed,
}: {
  me: WikiMeDto | undefined;
  badges: Partial<Record<NavBadgeKey, number | null>>;
  drawerOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
}): JSX.Element {
  // Свёрнутый рейл при наведении показывает плавающие плашки-подписи разом
  // напротив каждой иконки (кликабельные ссылки, как в CRM).
  const [hoverExpand, setHoverExpand] = useState(false);
  const [pills, setPills] = useState<Array<{ top: number; label: string; active: boolean; to: string }>>([]);
  const asideRef = useRef<HTMLElement | null>(null);
  const pillsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPillsHide = (): void => {
    if (pillsTimer.current) {
      clearTimeout(pillsTimer.current);
      pillsTimer.current = null;
    }
  };
  const schedulePillsHide = (): void => {
    cancelPillsHide();
    pillsTimer.current = setTimeout(() => {
      setHoverExpand(false);
      setPills([]);
    }, 160);
  };
  const computePills = (): void => {
    const el = asideRef.current;
    if (!el) return;
    const links = Array.from(el.querySelectorAll<HTMLElement>('.fk-navlink'));
    setPills(
      links
        .map((l) => {
          const r = l.getBoundingClientRect();
          return { top: r.top + r.height / 2, label: l.dataset.label ?? '', active: l.dataset.active === 'true', to: l.dataset.to ?? '' };
        })
        .filter((p) => p.label),
    );
  };
  useEffect(() => {
    if (!collapsed) {
      setHoverExpand(false);
      setPills([]);
    }
  }, [collapsed]);

  const groups = visibleNav(me);

  return (
    <>
      <aside
        ref={asideRef}
        className="fk-sidebar fk-hub-sidebar fk-scroll"
        data-open={drawerOpen ? 'true' : 'false'}
        aria-label="Меню"
        onMouseEnter={() => {
          if (collapsed) {
            cancelPillsHide();
            computePills();
            setHoverExpand(true);
          }
        }}
        onMouseMove={() => {
          if (collapsed && hoverExpand) computePills();
        }}
        onMouseLeave={() => {
          if (collapsed) schedulePillsHide();
        }}
      >
        <button type="button" className="fk-hamburger fk-hub-iconbtn fk-hub-drawer-close" onClick={onClose} aria-label="Закрыть меню">
          <CloseOutlined />
        </button>

        <div className="fk-hub-sidebar-head">
          <Link to="/" className="fk-hub-logo-link" aria-label="FISHKA HUB — на главную">
            <HubLogo />
          </Link>
        </div>

        <nav className="fk-hub-nav">
          {groups.map((g) => (
            <div key={g.key} className="fk-hub-navgroup">
              <div className="fk-sidebar-label fk-hub-navgroup-title">{g.title}</div>
              {collapsed && <div className="fk-hub-navgroup-sep" />}
              {g.items.map((item) => (
                <SideNavLink key={item.to} item={item} count={item.badge ? badges[item.badge] : null} collapsed={collapsed} />
              ))}
            </div>
          ))}
        </nav>

        {/* «Нужна помощь?» переехала в плавающую кнопку (HelpFab) — здесь только подпись. */}
        <div className="fk-hub-sidebar-foot">
          <div className="fk-sidebar-onlyfull fk-hub-copy">© BRASOL · FISHKA HUB 2.0</div>
        </div>
      </aside>

      {collapsed && hoverExpand && pills.length > 0 && (
        <div className="fk-hover-pills">
          {pills.map((p, i) => (
            <Link
              key={i}
              to={p.to}
              className="fk-hover-pill"
              data-active={p.active ? 'true' : 'false'}
              style={{ top: p.top }}
              onMouseEnter={cancelPillsHide}
              onMouseLeave={schedulePillsHide}
              onClick={() => {
                cancelPillsHide();
                setHoverExpand(false);
                setPills([]);
              }}
            >
              {p.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Шапка ─────────────────────────────────────────────────────────

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

function Header({ me, onMenu }: { me: WikiMeDto | undefined; onMenu: () => void }): JSX.Element {
  return (
    <header className="fk-header fk-hub-header">
      {/* Гамбургер — выезжающее меню на планшете (768–900 px); на телефоне — «Ещё» в нижней панели. */}
      <button type="button" className="fk-hamburger fk-hub-iconbtn fk-hub-burger" onClick={onMenu} aria-label="Меню">
        <MenuOutlined />
      </button>

      {/* Поле поиска открывает CmdK (сам поиск — в оверлее: группы, клавиатура). */}
      <button type="button" className="fk-hub-search" onClick={() => openCmdK()} data-tour="search" aria-label="Поиск по знаниям, курсам, стандартам, файлам">
        <SearchOutlined className="fk-hub-search-ic" />
        <span className="fk-hub-search-ph">Поиск по знаниям, курсам, стандартам, файлам…</span>
        <kbd className="fk-hub-kbd">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
      </button>

      <div style={{ flex: 1 }} />

      <CreateMenu />
      <NotificationsBell />
      {me && <PointSelector />}
      <UserMenu me={me} />
    </header>
  );
}

// ─── Селектор торговой точки (из CRM) ───────────────────────────────

const ACTIVE_POINT_KEY = 'fk-active-point';

interface ActivePoint {
  id: string | null;
  name: string;
}

function loadActivePoint(): ActivePoint | null {
  try {
    const raw = localStorage.getItem(ACTIVE_POINT_KEY);
    return raw ? (JSON.parse(raw) as ActivePoint) : null;
  } catch {
    return null;
  }
}

/** «Город · Название» без дубля города (как formatPointName в CRM). */
function pointLabel(p: { name: string; city: string | null }): string {
  const city = (p.city ?? '').trim();
  const name = p.name.trim();
  if (!city) return name;
  if (!name || name.toLowerCase() === city.toLowerCase()) return city;
  const stripped = name.toLowerCase().startsWith(city.toLowerCase()) ? name.slice(city.length).replace(/^[\s,.·-]+/, '') : name;
  return stripped ? `${city} · ${stripped}` : city;
}

/**
 * Выбор активной точки (компактно в шапке). Список — из CRM с учётом доступа
 * (canSeeAll → вся сеть). Выбор — в localStorage `fk-active-point` + событие
 * `fk-active-point-change` для страниц, которые фильтруют по точке.
 */
function PointSelector(): JSX.Element | null {
  const [active, setActive] = useState<ActivePoint | null>(() => loadActivePoint());
  const q = useQuery({ queryKey: ['wiki', 'trading-points'], queryFn: () => openingApi.tradingPoints(), staleTime: 5 * 60_000, retry: false });
  const points = useMemo(() => q.data?.points ?? [], [q.data]);
  const canSeeAll = q.data?.canSeeAll ?? false;

  const save = (next: ActivePoint): void => {
    setActive(next);
    try {
      localStorage.setItem(ACTIVE_POINT_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent('fk-active-point-change', { detail: next }));
    } catch {
      /* noop */
    }
  };

  // Дефолт при первой загрузке: Архангельск-Новгородский → иначе первая точка.
  // Сохранённая точка не из списка (смотрим от лица партнёра, сменился
  // доступ) — тоже на дефолт, иначе в шапке висела бы чужая точка.
  useEffect(() => {
    if (points.length === 0) return;
    const stillValid = active && (active.id === null ? canSeeAll && points.length > 1 : points.some((p) => p.id === active.id));
    if (stillValid) return;
    const arh = points.find((p) => /архангельск/i.test(`${p.city ?? ''} ${p.name}`) && /новгородск/i.test(p.name));
    const def = arh ?? points[0];
    save({ id: def.id, name: pointLabel(def) });
  }, [points, active, canSeeAll]);

  if (q.isSuccess && points.length === 0) return null;

  const items: MenuProps['items'] = [
    ...(canSeeAll && points.length > 1 ? [{ key: '__all', label: 'Все точки', icon: <EnvironmentOutlined /> }] : []),
    ...points.map((p) => ({ key: p.id, label: pointLabel(p), icon: <EnvironmentOutlined /> })),
  ];
  const selectedKey = active?.id === null ? '__all' : (active?.id ?? '');

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        items,
        selectable: true,
        selectedKeys: [selectedKey],
        style: { maxHeight: 360, overflowY: 'auto' },
        onClick: ({ key }) => {
          if (key === '__all') save({ id: null, name: 'Все точки' });
          else {
            const p = points.find((x) => x.id === key);
            if (p) save({ id: p.id, name: pointLabel(p) });
          }
        },
      }}
    >
      <button type="button" className="fk-hub-point" title={active?.name ?? 'Точка'}>
        <EnvironmentOutlined style={{ color: 'var(--fk-orange)' }} />
        <span className="fk-hub-point-label">{active?.name ?? (q.isLoading ? 'Загрузка…' : 'Точка')}</span>
        <DownOutlined className="fk-hub-point-label" style={{ fontSize: 10, color: 'var(--fk-text-muted)' }} />
      </button>
    </Dropdown>
  );
}

// ─── Колокол уведомлений (in-app инбокс) ───────────────────────────

/**
 * Колокол со счётчиком непрочитанных (GET /inbox/count, раз в минуту) и
 * списком. Клик → прочитано + переход по link. Для сотрудника iiko бэк
 * отдаёт 0 / пустой список.
 */
function NotificationsBell(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const countQuery = useQuery({
    queryKey: ['wiki', 'notif', 'count'],
    queryFn: () => notificationsApi.inboxCount(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const listQuery = useQuery({
    queryKey: ['wiki', 'notif', 'list'],
    queryFn: () => notificationsApi.inbox(false),
    enabled: open,
    retry: false,
  });
  const unread = countQuery.data?.count ?? 0;
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['wiki', 'notif', 'count'] });
    void qc.invalidateQueries({ queryKey: ['wiki', 'notif', 'list'] });
  };
  const readMutation = useMutation({ mutationFn: (id: string) => notificationsApi.inboxRead(id), onSuccess: invalidate });
  const readAllMutation = useMutation({ mutationFn: () => notificationsApi.inboxReadAll(), onSuccess: invalidate });

  const onItemClick = (n: WikiInboxNotification): void => {
    if (!n.isRead) readMutation.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const items = listQuery.data ?? [];
  const panel = (
    <div className="fk-hub-pop fk-hub-bell-pop" style={{ padding: 0 }}>
      <div className="fk-hub-bell-head">
        <b>Уведомления</b>
        <span style={{ display: 'flex', gap: 12 }}>
          {unread > 0 && (
            <button type="button" className="fk-hub-linkbtn" onClick={() => readAllMutation.mutate()} disabled={readAllMutation.isPending}>
              Прочитать все
            </button>
          )}
          <Link to="/notifications" className="fk-hub-linkbtn" onClick={() => setOpen(false)}>
            <SettingOutlined /> Настроить
          </Link>
        </span>
      </div>
      <div className="fk-scroll" style={{ maxHeight: 380, overflowY: 'auto' }}>
        {listQuery.isLoading ? (
          <div className="fk-hub-muted" style={{ padding: 24, textAlign: 'center' }}>Загрузка…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '28px 16px' }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет уведомлений" />
          </div>
        ) : (
          items.map((n) => (
            <button key={n.id} type="button" className="fk-hub-bell-row" data-unread={n.isRead ? 'false' : 'true'} onClick={() => onItemClick(n)}>
              <span className="fk-hub-bell-ic">
                <BellOutlined />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 600, fontSize: 13, lineHeight: 1.3, color: 'var(--fk-text)' }}>{n.title}</span>
                {n.body && <span className="fk-hub-ellipsis" style={{ display: 'block', fontSize: 12, color: 'var(--fk-text-2)', marginTop: 2 }}>{n.body}</span>}
                <span style={{ display: 'block', fontSize: 11, color: 'var(--fk-text-muted)', marginTop: 3 }}>{fmtNotifDate(n.createdAt)}</span>
              </span>
              {!n.isRead && (
                <span
                  role="button"
                  aria-label="Отметить прочитанным"
                  className="fk-hub-bell-read"
                  onClick={(e) => {
                    e.stopPropagation();
                    readMutation.mutate(n.id);
                  }}
                >
                  <CheckOutlined />
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} placement="bottomRight" popupRender={() => panel}>
      <button type="button" aria-label={unread ? `Уведомления: ${unread} непрочитанных` : 'Уведомления'} className="fk-hub-iconbtn fk-hub-bell">
        <Badge count={unread} size="small" overflowCount={99} offset={[4, -4]}>
          <BellOutlined style={{ fontSize: 17, color: 'var(--fk-text-2)' }} />
        </Badge>
      </button>
    </Dropdown>
  );
}

/** Дата уведомления: «сегодня HH:MM», «вчера HH:MM», иначе «дд.мм». */
function fmtNotifDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(yest.getDate() - 1);
  const hhmm = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return `сегодня ${hhmm}`;
  if (d.toDateString() === yest.toDateString()) return `вчера ${hhmm}`;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

// ─── Меню пользователя ──────────────────────────────────────────────

/** «АО» из «Алексей Орлов» / «Орлов Алексей Петрович» — две первые буквы слов. */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  return (parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || 'У').slice(0, 2);
}

function UserMenu({ me }: { me: WikiMeDto | undefined }): JSX.Element | null {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { mode, setMode } = useThemeMode();
  const [viewAsOpen, setViewAsOpen] = useState(false);

  const logoutMutation = useMutation({
    mutationFn: async () => {
      if (me?.type === 'crm_user') await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      else await authApi.logout();
    },
    onSuccess: async () => {
      qc.clear();
      try {
        Object.keys(localStorage)
          .filter((key) => key.startsWith('wiki-draft:'))
          .forEach((key) => localStorage.removeItem(key));
        if ('caches' in window) await Promise.all((await caches.keys()).filter((key) => key.startsWith('brasol-wiki-')).map((key) => caches.delete(key)));
      } catch {
        /* Выход не блокируем, если браузер запретил локальное хранилище. */
      }
      if (me?.type === 'crm_user') window.location.href = `${CRM_URL}/login`;
      else navigate('/login', { replace: true });
    },
  });

  if (!me) return null;
  const isCrm = me.type === 'crm_user';
  // «От лица пользователя» (12.10.2026): ФИО и роль — тёзок не спутать.
  const subtitle = me.viewAs
    ? `Просмотр: ${me.viewAs.userName ? `${me.viewAs.userName} · ${me.viewAs.roleName}` : me.viewAs.partnerName || me.viewAs.roleName}`
    : isCrm
      ? me.roleName || 'Пользователь CRM'
      : [me.roleName, me.tradingPointName].filter(Boolean).join(' · ') || 'Сотрудник';

  const items: MenuProps['items'] = [
    ...visibleUserMenu(me).map((i) => {
      const Icon = i.icon;
      return { key: i.to, icon: <Icon />, label: i.label, onClick: () => navigate(i.to) };
    }),
    { type: 'divider' as const },
    ...(isCrm
      ? [{ key: 'crm', icon: <AppstoreOutlined />, label: 'В CRM', onClick: () => { window.location.href = CRM_URL; } }]
      : []),
    // «Смотреть от лица партнёра» (владелец 07.10.2026) — как в CRM.
    ...(isCrm && me.viewAs
      ? [{ key: 'view-as-stop', icon: <EyeOutlined />, danger: true, label: 'Вернуться к себе', onClick: () => void stopViewAs() }]
      : []),
    ...(isCrm && !me.viewAs && me.canViewAs
      ? [{ key: 'view-as', icon: <EyeOutlined />, label: 'Смотреть от лица партнёра…', onClick: () => setViewAsOpen(true) }]
      : []),
    {
      key: 'theme',
      icon: <BgColorsOutlined />,
      label: `Тема: ${THEME_LABEL[mode].toLowerCase()}`,
      children: THEME_ORDER.map((m) => ({
        key: `theme:${m}`,
        label: THEME_LABEL[m],
        icon: m === mode ? <CheckOutlined /> : <span style={{ display: 'inline-block', width: 14 }} />,
        onClick: () => setMode(m),
      })),
    },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, danger: true, label: 'Выйти', onClick: () => logoutMutation.mutate() },
  ];

  return (
    <>
      <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
        <button type="button" className="fk-hub-user" data-tour="user" aria-label={`Меню пользователя: ${me.fullName}`}>
          <Avatar size={36} src={isCrm ? (me.avatarUrl ?? undefined) : undefined} className="fk-hub-avatar">
            {initialsOf(me.fullName)}
          </Avatar>
          <span className="fk-hub-user-text">
            <span className="fk-hub-user-name">{me.fullName}</span>
            <span className="fk-hub-user-sub">{subtitle}</span>
          </span>
          <DownOutlined className="fk-hub-user-text" style={{ fontSize: 11, color: 'var(--fk-text-muted)' }} />
        </button>
      </Dropdown>
      {viewAsOpen ? <ViewAsModal open={viewAsOpen} onClose={() => setViewAsOpen(false)} /> : null}
    </>
  );
}
